import { ICartGroup, ICartItem, ITotalData } from '@/api/types/cart';
import { useStore } from '@/providers/app-store-provider';
import { useCallback, useMemo, useState } from 'react';

export const useCartState = () => {
  // 从 store 获取购物车数据
  const canBuyList = useStore((state) => state.canBuyList);
  const canNotBuyList = useStore((state) => state.canNotBuyList);
  const canBuyCartsList = useStore((state) => state.canBuyCartsList);
  const currency = useStore((state) => state.currency);
  const publicResourceData = useStore((state) => state.publicResourceData);

  // Out of Stock & Invalid Items 包含的购物车为第二购物车
  // 第二购物车列表 cartCanNotBuyList 表示第二购物车（Out of Stock & Invalid Items 下面的购物车列表）渲染的所有品. 跟接口的 can_not_sell_list 字段对应
  // 第一购物车列表 cartCanBuyList 表示第一购物车（Out of Stock & Invalid Items 上面的购物车列表）渲染的所有品。 跟接口的 list 字段对应
  // 所有（非编辑模式下）可勾选的购物车列表 canBuyCartsList （数据源在store里）。 跟接口的 can_buy_list 字段对应
  // UI 状态
  const [cartCanBuyList, setCartCanBuyList] = useState<ICartGroup[]>([]);
  const [cartCanNotBuyList, setCartCanNotBuyList] = useState<ICartItem[]>([]);
  const [allCheck, setAllCheck] = useState(false);
  const [showMoreInvalid, setShowMoreInvalid] = useState(false);
  const [isInitData, setIsInitData] = useState(false);

  // 计算数据
  const [totalData, setTotalData] = useState<ITotalData>({
    total: '0.00',
    discount: '0.00',
    subtotal: '0.00',
  });
  const [promotion, setPromotion] = useState<any[]>([]);

  // 购物车默认勾选配置
  const cartDefaultCheckAll = (publicResourceData?.info as any)?.cart_default_check_all || 1;

  // 计算选中的可购买商品
  const selectedCanBuy = useMemo(() => {
    const selected: ICartItem[] = [];
    cartCanBuyList.forEach((group) => {
      if (group.selected && group.carts) {
        selected.push(...group.carts);
      }
    });
    return selected;
  }, [cartCanBuyList]);

  // 计算总数量（可购买列表中所有商品的数量，不管是否勾选）
  const allQty = useMemo(() => {
    return cartCanBuyList.reduce((total, item) => {
      if (item.carts) {
        return total + item.carts.reduce((subTotal, cartItem) => subTotal + cartItem.qty, 0);
      }
      return total;
    }, 0);
  }, [cartCanBuyList]);

  // 处理不可购买商品选中状态变化
  const handleInvalidSelectChange = useCallback(
    (index: number, checked: boolean) => {
      const updatedList = [...cartCanNotBuyList];
      updatedList[index] = { ...updatedList[index], selected: checked };
      setCartCanNotBuyList(updatedList);

      // 更新全选状态
      const allSelected =
        cartCanBuyList.every((item) => item.selected) && updatedList.every((item) => item.selected);
      setAllCheck(allSelected);
    },
    [cartCanBuyList, cartCanNotBuyList],
  );

  return {
    // 状态
    cartCanBuyList,
    cartCanNotBuyList,
    allCheck,
    showMoreInvalid,
    isInitData,
    totalData,
    promotion,
    cartDefaultCheckAll,

    // 计算属性
    selectedCanBuy,
    allQty,

    // Store 数据
    canBuyList,
    canNotBuyList,
    canBuyCartsList,
    currency,
    publicResourceData,

    // 状态更新方法
    setCartCanBuyList,
    setCartCanNotBuyList,
    setAllCheck,
    setShowMoreInvalid,
    setIsInitData,
    setTotalData,
    setPromotion,

    // 事件处理方法
    handleInvalidSelectChange,
  };
};
